import { escapeHtml, presenceDotHtml } from '@colplat/ui-web';
import { apiFetch } from '../api.js';
import { appHref } from '../globals.js';
import { knownOffline } from '../offlineReads.js';
import { readCached } from '../offlineReads.js';
import { bootstrapWebledgerClient, webledgerShellHtml, wireBackButton } from '../webledgerClient.js';
import {
  splitNumbering, timelineHtml, type HistoryContext, type TransactionHistory,
} from './webledgerHistory.js';

/**
 * The full-page change history for one transaction —
 * `/webledger/transactions/:id/history`.
 *
 * The same timeline the transaction form slides over, on its own route so it
 * can be linked, bookmarked and sent to someone ("look at what happened to
 * this entry on the 8th"). Read-only: restoring belongs with the form, where
 * a lease and the current values are already in hand.
 */

interface AccountDto { id: string; name: string }
interface CurrencyDto { code: string; symbol: string; fraction: number; name: string }
interface TransactionDto { id: string; description: string; txDate: string; currencyCode: string }

/** "just now" / "6 minutes ago" — same wording as the slide-over. */
function timeAgo(iso: string): string {
  const seconds = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000));
  if (seconds < 60) return 'just now';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

export async function mountWebledgerTransactionHistory(root: HTMLElement, txId: string): Promise<void> {
  root.innerHTML = webledgerShellHtml('transactions', `
    <div class="cf-card cf-card--bleed p-3">
      <div class="d-flex justify-content-between align-items-start mb-2 gap-2">
        <div>
          <div class="cf-h">Change history</div>
          <div class="fw-semibold" id="thTitle">…</div>
          <div class="text-secondary small" id="thSub"></div>
        </div>
        <a class="btn btn-sm btn-outline-secondary" href="${appHref('/webledger/transactions')}">Back to transactions</a>
      </div>
      <div id="thBody"><p class="text-secondary small mb-0">Loading…</p></div>
    </div>`) + `<div class="cf-sr" aria-live="polite" aria-atomic="true"></div>`;
  wireBackButton(root);

  const client = await bootstrapWebledgerClient(root);
  if (!client) return;
  const { token, docCache } = client;
  const body = root.querySelector<HTMLElement>('#thBody')!;

  // The transaction itself and the reference lists come from the cache like
  // everywhere else, so the page renders its heading offline even though the
  // history below it cannot load.
  const [accountsRead, currenciesRead, txRead] = await Promise.all([
    readCached<{ accounts: AccountDto[] }>(docCache, 'accounts', '/apps/webledger/accounts', token),
    readCached<{ currencies: CurrencyDto[] }>(docCache, 'currencies', '/apps/webledger/currencies', token),
    readCached<{ transaction: TransactionDto }>(
      docCache, `transaction:${txId}`, `/apps/webledger/transactions/${txId}`, token),
  ]);
  const accounts = accountsRead.ok ? accountsRead.data.accounts : [];
  const currencies = currenciesRead.ok ? currenciesRead.data.currencies : [];
  const tx = txRead.ok ? txRead.data.transaction : undefined;

  root.querySelector('#thTitle')!.textContent = tx?.description || 'Transaction';
  root.querySelector('#thSub')!.textContent = tx ? `${tx.txDate} · ${tx.currencyCode}` : '';

  // Cached like every other read. The transactions page primes history for
  // the whole ledger in the background (ADR-0011), so this page usually has
  // its answer already — and still has one offline.
  const cached = docCache.get<TransactionHistory>(`history:${txId}`);
  let history = cached?.value;
  let staleSince = cached?.cachedAt;

  if (!knownOffline() && client.transport.state === 'open') {
    const res = await apiFetch(`/apps/webledger/transactions/${txId}/history`, { token }).catch(() => undefined);
    if (res?.ok) {
      history = (await res.json()) as TransactionHistory;
      staleSince = undefined;
    } else if (!history) {
      body.innerHTML = res?.status === 404
        ? '<p class="text-secondary small mb-0">That transaction does not exist.</p>'
        : '<p class="text-secondary small mb-0">Could not load the change history.</p>';
      return;
    }
  }
  if (!history) {
    body.innerHTML = '<p class="text-secondary small mb-0">You are offline, and this transaction\u2019s '
      + 'history has not reached this device yet.</p>';
    return;
  }

  const ctx: HistoryContext = {
    accountName: (id) => accounts.find((a) => a.id === id)?.name ?? id.slice(0, 8),
    currency: (code) => currencies.find((c) => c.code === code),
    txCurrencyCode: tx?.currencyCode ?? '',
    splitNumber: splitNumbering(history.commits),
  };

  function paint(): void {
    const staleNote = staleSince
      ? `<div class="alert alert-warning py-2 px-3 mb-2 small" role="status">Offline — this is the history `
        + `as saved on this device. Anything changed since is not here.</div>`
      : '';
    body.innerHTML = staleNote + timelineHtml({
      history: history!, ctx,
      // Restoring needs a lease and the live record; it lives with the form.
      canRestore: false,
      escapeHtml, presenceDotHtml, timeAgo,
    });
  }
  paint();
}
